import { Mark } from 'tiptap'
import { getMarkAttrs } from 'tiptap-utils'
import { TextSelection, AllSelection } from 'prosemirror-state'

function markApplies(doc, ranges, type) {
  for (let i = 0; i < ranges.length; i++) {
    const { $from, $to } = ranges[i]
    let can = $from.depth === 0 ? doc.type.allowsMarkType(type) : false
    doc.nodesBetween($from.pos, $to.pos, (node) => {
      if (can) return false

      can = node.inlineContent && node.type.allowsMarkType(type)
      return true
    })
    if (can) return true
  }
  return false
}

function applyMark(tr, markType, attrs) {
  if (!tr.selection || !tr.doc || !markType) return tr

  const { empty, $cursor, ranges } = tr.selection

  if ((empty && !$cursor) || !markApplies(tr.doc, ranges, markType)) return tr

  if ($cursor) {
    tr = tr.removeStoredMark(markType)
    return attrs ? tr.addStoredMark(markType.create(attrs)) : tr
  }

  let has = false
  for (let i = 0; !has && i < ranges.length; i++) {
    const { $from, $to } = ranges[i]
    has = tr.doc.rangeHasMark($from.pos, $to.pos, markType)
  }

  for (let i = 0; i < ranges.length; i++) {
    const { $from, $to } = ranges[i]
    if (has) {
      tr = tr.removeMark($from.pos, $to.pos, markType)
    }
    if (attrs) {
      tr = tr.addMark($from.pos, $to.pos, markType.create(attrs))
    }
  }

  return tr
}

export const DEFAULT_FONT_SIZES = [
  '8',
  '10',
  '12',
  '14',
  '16',
  '18',
  '20',
  '24',
  '30',
  '36',
  '48',
  '60',
  '72',
]
const DEFAULT_FONT_SIZE = 'default'

const SIZE_PATTERN = /([\d.]+)(px|pt)|\D/i

function convertToPX(styleValue) {
  const matches = styleValue.match(SIZE_PATTERN)
  if (!matches) return 12
  const value = /^\D/.test(matches[0]) === true ? 12 : matches[1]
  if (!value) return ''
  return value
}

function setFontSize(tr, type, fontSize) {
  const { selection } = tr

  if (
    !(selection instanceof TextSelection || selection instanceof AllSelection)
  ) {
    return tr
  }

  const attrs =
    fontSize && fontSize !== DEFAULT_FONT_SIZE ? { px: fontSize } : null
  tr = applyMark(tr, type, attrs)

  // Apply font size to list items
  const { $from, $to } = selection
  tr.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
    if (node.type.name === 'list_item') {
      const existingStyle = node.attrs.style || ''
      const newStyle = existingStyle
        .replace(/--li-font-size:\s*[\d.]+px;/, '')
        .trim()
      const updatedStyle = `${newStyle} --li-font-size: ${fontSize}px;`.trim()
      tr = tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        style: updatedStyle,
      })
    }
  })

  return tr
}

export function findActiveFontSize(state) {
  const { schema, selection, tr } = state
  const markType = schema.marks.font_size

  if (!markType) return DEFAULT_FONT_SIZE

  const { empty } = selection

  if (empty) {
    const storedMarks =
      tr.storedMarks ||
      state.storedMarks ||
      (selection instanceof TextSelection &&
        selection.$cursor &&
        selection.$cursor.marks &&
        selection.$cursor.marks()) ||
      []

    const sm = storedMarks.find((m) => m.type === markType)
    return sm ? String(sm.attrs.px || DEFAULT_FONT_SIZE) : DEFAULT_FONT_SIZE
  }

  const attrs = getMarkAttrs(state, markType)
  const fontSize = attrs.px
  if (fontSize) return String(fontSize)

  return DEFAULT_FONT_SIZE
}

export default class FontSize extends Mark {
  get name() {
    return 'font_size'
  }

  get defaultOptions() {
    return {
      fontSizes: DEFAULT_FONT_SIZES,
    }
  }

  get schema() {
    return {
      attrs: {
        px: '',
      },
      inline: true,
      group: 'inline',
      parseDOM: [
        {
          style: 'font-size',
          getAttrs: (fontSize) => {
            const attrs = {}
            if (!fontSize) return attrs
            const px = convertToPX(fontSize)
            if (!px) return attrs

            return {
              px,
            }
          },
        },
      ],
      toDOM(node) {
        const { px } = node.attrs
        const attrs = {}

        if (px) {
          attrs.style = `font-size: ${px}px`
        }
        return ['span', attrs, 0]
      },
    }
  }

  commands({ type }) {
    return (fontSize) => (state, dispatch) => {
      let { tr } = state
      tr = setFontSize(state.tr.setSelection(state.selection), type, fontSize)
      if (tr.docChanged || tr.storedMarksSet) {
        dispatch && dispatch(tr)
        return true
      }
      return false
    }
  }
}
